import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Avatar,
  Snackbar,
  Container,
  alpha,
  useTheme,
  Chip,
  Stack,
  Divider,
  Modal,
  TextField,
  MenuItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { Home, Laptop, CalendarToday, AccessTime, Note, Cancel, Add, Close, CalendarMonth } from "@mui/icons-material";
import Loading from "../../components/loading/loading";
import { useLocation, useNavigate } from "react-router-dom";
import {
  clearSession,
  getStudentSessions,
  cancelSession,
  createSession,
} from "../../state/slices/session";
import { Pending } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { Alert } from "@mui/material";
import DateAvailabilityCalendar from "../../components/calendar/DateAvailabilityCalendar";
import { DemoContainer, DemoItem } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { StaticDatePicker } from "@mui/x-date-pickers/StaticDatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";

const TUTORING_TYPE = {
  IN_PERSON: "IN_PERSON",
  ONLINE: "REMOTE",
};

const Student_Detail_class_room = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const tutor = location.state?.tutor;
  const { t } = useTranslation();
  const theme = useTheme();
  const { sessions, loading, error, success, session } = useSelector(
    (state) => state.session
  );
  const [open, setOpen] = React.useState(false);
  const [meeting, setMeeting] = React.useState({
    date: dayjs(),
    startTime: dayjs(),
    endTime: dayjs().add(1, "hour"),
    tutoring_type: "",
    note: "",
  });
  const [errors, setErrors] = React.useState({});
  const [openAvailability, setOpenAvailability] = React.useState(false);

  const dispatch = useDispatch();

  const tutorId = tutor?.tutor?.id;
  const isTutorInactive =
    tutor?.tutor?.currentStatus === t('tutorProfile.inActive') || tutor?.tutor?.currentStatus === 'In Active';

  // Handle case where location.state is null
  React.useEffect(() => {
    if (!location.state || !location.state.tutor) {
      navigate('/student-class-room', { replace: true });
    }
  }, [location.state, navigate]);

  // Fetch sessions on mount and when tutorId changes
  React.useEffect(() => {
    if (tutorId) {
      dispatch(getStudentSessions(tutorId));
    }
  }, [tutorId, dispatch]);

  // Handle session creation success
  React.useEffect(() => {
    if (session && tutorId) {
      dispatch(clearSession());
      dispatch(getStudentSessions(tutorId));
    }
  }, [session, tutorId, dispatch]);

  // Handle other success messages (only for create/cancel, not for fetch)
  React.useEffect(() => {
    // Only refetch if success is from create/cancel, NOT from fetch (to avoid infinite loop)
    if (success && tutorId && success !== "Sessions fetched successfully") {
      dispatch(clearSession());
      dispatch(getStudentSessions(tutorId));
    }
  }, [success, tutorId, dispatch]);

  // Early return after all hooks
  if (!location.state || !location.state.tutor) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Loading />
      </Box>
    );
  }

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setMeeting({
      date: dayjs(),
      startTime: dayjs(),
      endTime: dayjs().add(1, "hour"),
      tutoring_type: "",
      note: "",
    });
    setErrors({});
  };

  const combineDateAndTime = (date, time) => {
    return dayjs(date)
      .hour(time.hour())
      .minute(time.minute())
      .second(0)
      .millisecond(0);
  };

  const validate = () => {
    let temp = {};
    temp.tutoringType = meeting.tutoring_type
      ? ""
      : t('classRoom.tutoringTypeRequired');
    temp.note = meeting.note ? "" : t('classRoom.noteRequired');
    setErrors({ ...temp });
    return Object.values(temp).every((x) => x === "");
  };

  const handleSubmit = () => {
    setErrors({});
    const isValid = validate();
    if (!isValid) {
      return;
    }
    
    try {
      const startDateTime = combineDateAndTime(meeting.date, meeting.startTime);
      const endDateTime = combineDateAndTime(meeting.date, meeting.endTime);
      
      if (!endDateTime || !startDateTime) {
        setErrors({ timeRange: 'Invalid date/time values' });
        return;
      }
      
      if (endDateTime.valueOf() <= startDateTime.valueOf()) {
        setErrors({ timeRange: t('classRoom.endTimeMustBeAfterStartTime') || 'End time must be after start time' });
        return;
      }
      
      const newMeeting = {
        tutorId: tutor.tutor.id,
        tutoringType: meeting.tutoring_type,
        note: meeting.note,
        startingTime: startDateTime.toISOString(),
        endingTime: endDateTime.toISOString(),
      };
      
      dispatch(createSession(newMeeting));
      handleClose();
    } catch (error) {
      console.error('Error submitting session:', error);
      setErrors({ submit: 'Failed to request session. Please try again.' });
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: alpha(theme.palette.primary.main, 0.03),
        pb: 4,
      }}
    >
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={Boolean((success && success !== "Sessions fetched successfully") || error)}
        autoHideDuration={3000}
        onClose={() => {
          dispatch(clearSession());
        }}
      >
        <Alert 
          severity={success ? "success" : "error"}
          sx={{ borderRadius: 2 }}
        >
          {success || error}
        </Alert>
      </Snackbar>

      <Container maxWidth="lg" sx={{ pt: 4 }}>
        <Card
          elevation={0}
          sx={{
            borderRadius: 4,
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            mb: 4,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ p: 4, textAlign: 'center', bgcolor: 'white' }}>
            <Avatar
              alt={tutor.tutor.firstName + " " + tutor.tutor.lastName}
              src={tutor.tutor.profileImage}
              sx={{
                width: 120,
                height: 120,
                mx: 'auto',
                mb: 2,
                border: `4px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              }}
            />
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: theme.palette.primary.main,
                mb: 3,
              }}
            >
              {tutor.tutor.firstName} {tutor.tutor.lastName} {t('classRoom.classRoom')}
            </Typography>
            <Stack direction="row" spacing={2} justifyContent="center" sx={{ flexWrap: 'wrap', gap: 2 }}>
              <Button
                variant="contained"
                onClick={handleOpen}
                startIcon={<Add />}
                sx={{
                  borderRadius: 3,
                  py: 1.5,
                  px: 3,
                  fontWeight: 700,
                  fontSize: '1rem',
                  boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 6px 25px ${alpha(theme.palette.primary.main, 0.4)}`,
                  }
                }}
              >
                {t('classRoom.requestSession') || 'Request Session'}
              </Button>
              {!isTutorInactive && tutor?.tutor?.dateAvailability && tutor.tutor.dateAvailability.length > 0 && (
                <Button
                  variant="outlined"
                  onClick={() => setOpenAvailability(true)}
                  startIcon={<CalendarMonth />}
                  sx={{
                    borderRadius: 3,
                    py: 1.5,
                    px: 3,
                    fontWeight: 700,
                    fontSize: '1rem',
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.main,
                    '&:hover': {
                      borderColor: theme.palette.primary.dark,
                      bgcolor: alpha(theme.palette.primary.main, 0.05),
                      transform: 'translateY(-2px)',
                    }
                  }}
                >
                  {t('detailResult.showAvailability') || 'Show Availability'}
                </Button>
              )}
            </Stack>
          </Box>
        </Card>

        {/* Request Session Modal */}
        <Modal 
          open={open} 
          onClose={handleClose}
          BackdropProps={{
            sx: {
              bgcolor: alpha('#000', 0.5),
              backdropFilter: 'blur(4px)',
            }
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '95%', md: 900 },
              bgcolor: 'background.paper',
              borderRadius: 4,
              boxShadow: `0 20px 60px ${alpha(theme.palette.primary.main, 0.3)}`,
              maxHeight: '90vh',
              overflowY: 'auto',
              border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            }}
          >
            <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                {t('classRoom.requestSession') || 'Request Session'}
              </Typography>
              <IconButton onClick={handleClose} sx={{ color: 'text.secondary' }}>
                <Close />
              </IconButton>
            </Box>
            
            <Box sx={{ p: 3 }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                        borderRadius: 3,
                        p: 2,
                        bgcolor: alpha(theme.palette.primary.main, 0.02),
                      }}
                    >
                      <DemoContainer components={["StaticDatePicker"]}>
                        <DemoItem label={t('classRoom.chooseDate')}>
                          <StaticDatePicker
                            orientation="portrait"
                            defaultValue={meeting.date}
                            onChange={(newValue) => {
                              setMeeting({ ...meeting, date: newValue });
                            }}
                          />
                        </DemoItem>
                      </DemoContainer>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Stack spacing={3}>
                      <Box
                        sx={{
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                          borderRadius: 3,
                          p: 2,
                          bgcolor: alpha(theme.palette.primary.main, 0.02),
                        }}
                      >
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <TimePicker
                              label={t('classRoom.startTime')}
                              value={meeting.startTime}
                              onChange={(newValue) => {
                                setMeeting({ ...meeting, startTime: newValue });
                              }}
                              sx={{ width: '100%' }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TimePicker
                              label={t('classRoom.endTime')}
                              value={meeting.endTime}
                              onChange={(newValue) => {
                                setMeeting({ ...meeting, endTime: newValue });
                              }}
                              sx={{ width: '100%' }}
                            />
                          </Grid>
                        </Grid>
                        {errors.timeRange && (
                          <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                            {errors.timeRange}
                          </Typography>
                        )}
                        {errors.submit && (
                          <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                            {errors.submit}
                          </Typography>
                        )}
                      </Box>

                      <TextField
                        fullWidth
                        label={t('classRoom.tutoringType')}
                        value={meeting.tutoring_type}
                        error={Boolean(errors["tutoringType"])}
                        helperText={errors["tutoringType"]}
                        onChange={(event) => {
                          setMeeting({
                            ...meeting,
                            tutoring_type: event.target.value,
                          });
                        }}
                        select
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            bgcolor: alpha(theme.palette.primary.main, 0.03),
                          }
                        }}
                      >
                        <MenuItem value={TUTORING_TYPE.IN_PERSON}>
                          {t('common.inPerson')}
                        </MenuItem>
                        <MenuItem value={TUTORING_TYPE.ONLINE}>{t('common.online')}</MenuItem>
                      </TextField>

                      <TextField
                        multiline
                        minRows={4}
                        fullWidth
                        label={t('classRoom.note')}
                        value={meeting.note}
                        onChange={(event) => {
                          setMeeting({
                            ...meeting,
                            note: event.target.value,
                          });
                        }}
                        error={Boolean(errors["note"])}
                        helperText={errors["note"]}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            bgcolor: alpha(theme.palette.primary.main, 0.03),
                          }
                        }}
                      />
                    </Stack>
                  </Grid>
                </Grid>

                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}>
                  <Button
                    variant="outlined"
                    onClick={handleClose}
                    sx={{ 
                      borderRadius: 2,
                      py: 1.25,
                      px: 3,
                      fontWeight: 600,
                    }}
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    type="button"
                    sx={{
                      borderRadius: 2,
                      py: 1.25,
                      px: 3,
                      fontWeight: 600,
                      boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 6px 25px ${alpha(theme.palette.primary.main, 0.4)}`,
                      }
                    }}
                  >
                    {t('classRoom.requestSession') || 'Request Session'}
                  </Button>
                </Box>
              </LocalizationProvider>
            </Box>
          </Box>
        </Modal>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <Loading />
          </Box>
        ) : sessions.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '400px',
              textAlign: 'center',
            }}
          >
            <Typography variant="h5" sx={{ color: 'text.secondary', fontWeight: 600, mb: 1 }}>
              {t('classRoom.noSession')}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t('classRoom.noSessionsAvailable')}
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {sessions.map((meeting) => (
              <Grid item xs={12} sm={6} md={4} key={meeting._id}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    borderRadius: 3,
                    boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: `0 8px 30px ${alpha(theme.palette.primary.main, 0.2)}`,
                      borderColor: theme.palette.primary.main,
                    }
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Stack spacing={2}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <CalendarToday sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                        <Box>
                          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                            {t('classRoom.date')}
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {dayjs(meeting.startingTime).format("DD/MM/YYYY")}
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <AccessTime sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                        <Box>
                          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                            {t('classRoom.time')}
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {dayjs(meeting.startingTime).format("hh:mm a")} - {dayjs(meeting.endingTime).format("hh:mm a")}
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        {meeting.tutoringType === "REMOTE" ? (
                          <Laptop sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                        ) : (
                          <Home sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                        )}
                        <Box>
                          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                            {t('classRoom.tutoringType')}
                          </Typography>
                          <Chip
                            label={meeting.tutoringType === "REMOTE" ? t('common.online') : t('common.inPerson')}
                            size="small"
                            sx={{
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                              color: theme.palette.primary.main,
                              fontWeight: 600,
                            }}
                          />
                        </Box>
                      </Box>

                      {meeting.note && (
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                          <Note sx={{ color: theme.palette.primary.main, fontSize: 20, mt: 0.5 }} />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600, mb: 0.5 }}>
                              {t('classRoom.note')}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              {meeting.note}
                            </Typography>
                          </Box>
                        </Box>
                      )}

                      {meeting.status === 'PENDING_APPROVAL' && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Pending sx={{ color: theme.palette.warning.main, fontSize: 20 }} />
                          <Chip
                            label={t('classRoom.pendingApproval') || 'Pending Approval'}
                            size="small"
                            sx={{
                              bgcolor: alpha(theme.palette.warning.main, 0.1),
                              color: theme.palette.warning.main,
                              fontWeight: 600,
                            }}
                          />
                        </Box>
                      )}

                      <Divider sx={{ my: 1 }} />

                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {meeting.status === 'PENDING_APPROVAL' ? (
                          <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic', width: '100%', textAlign: 'center' }}>
                            {t('classRoom.waitingForTutorApproval') || 'Waiting for tutor approval...'}
                          </Typography>
                        ) : (
                          <>
                            {meeting.tutoringType === "REMOTE" && meeting.status === 'SCHEDULED' && (
                              <Button
                                disabled={
                                  meeting.startingTime > dayjs() ||
                                  meeting.endingTime < dayjs()
                                }
                                onClick={() => {
                                  navigate(`/session/${meeting._id}/join`);
                                }}
                                variant="contained"
                                size="small"
                                sx={{
                                  flex: 1,
                                  borderRadius: 2,
                                  fontWeight: 600,
                                  textTransform: 'capitalize',
                                }}
                              >
                                {t('classRoom.joinSession')}
                              </Button>
                            )}
                            {meeting.status === 'SCHEDULED' && (
                              <Button
                                disabled={meeting.startingTime < dayjs()}
                                onClick={() => {
                                  dispatch(cancelSession(meeting._id));
                                  if (tutorId) {
                                    dispatch(getStudentSessions(tutorId));
                                  }
                                }}
                                variant="outlined"
                                color="error"
                                size="small"
                                startIcon={<Cancel />}
                                sx={{
                                  flex: 1,
                                  borderRadius: 2,
                                  fontWeight: 600,
                                  textTransform: 'capitalize',
                                }}
                              >
                                {t('classRoom.cancel')}
                              </Button>
                            )}
                          </>
                        )}
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Availability Calendar Dialog */}
        <Dialog
          open={openAvailability}
          onClose={() => setOpenAvailability(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 4,
              boxShadow: `0 20px 60px ${alpha(theme.palette.primary.main, 0.3)}`,
            }
          }}
        >
          <DialogTitle sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2,
            pb: 2,
            borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
          }}>
            <CalendarMonth sx={{ fontSize: 28, color: theme.palette.primary.main }} />
            <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
              {t('tutorProfile.calendar') || 'Tutor Calendar'}
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            {tutor?.tutor?.dateAvailability && (
              <DateAvailabilityCalendar
                value={tutor.tutor.dateAvailability || []}
                disabled={true}
              />
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 2 }}>
            <Button
              onClick={() => setOpenAvailability(false)}
              variant="contained"
              sx={{
                borderRadius: 2,
                px: 3,
                fontWeight: 600,
              }}
            >
              {t('common.close') || 'Close'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default Student_Detail_class_room;
